import React from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { getBlogPosts } from "@/lib/blogContent";

const Blog = () => {
  const { language, t } = useLanguage();
  const posts = getBlogPosts(language);

  return (
    <div data-testid="blog-page">
      <section className="relative pt-24 pb-12 lg:pt-32 lg:pb-16 bg-white">
        <div className="absolute inset-0 hex-pattern opacity-100 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.22em] text-teal-700 mb-4">{t("blog.kicker")}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight max-w-3xl">
            {t("blog.titlePrefix")} <em className="not-italic text-teal-700">{t("blog.titleEm")}</em>.
          </h1>
          <p className="mt-5 text-slate-600 max-w-2xl">
            {t("blog.lead")}
          </p>
        </div>
      </section>

      <section className="pb-24 lg:pb-32 bg-white" data-testid="blog-list">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {posts.length === 0 ? (
            <div className="text-slate-600">{t("blog.empty")}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p, idx) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group flex flex-col rounded-xl overflow-hidden bg-white border border-slate-200 card-hover"
                  data-testid={`blog-card-${idx}`}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[11px] uppercase tracking-[0.12em] text-teal-800 border border-teal-100">
                      {p.category}
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span>{p.published_at}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1">
                        <Clock size={12} /> {p.read_minutes} {t("blog.minutes")}
                      </span>
                    </div>
                    <h2 className="mt-3 font-display text-xl text-slate-900 leading-snug">
                      {p.title}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">
                      {p.excerpt}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-teal-700">
                      {t("blog.readArticle")} <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
